// Master-mix render — a leveled sum of positioned audio, and the WAV/AIFF
// decoding underneath it.
//
// Neither DAW gives us a master bounce, so we build one: sum every audible
// piece of audio, each scaled by its track's mixer level and laid at its own
// offset, then guard against clipping. Output is 16-bit PCM WAV — universally
// playable in the Browse preview's HTML5 audio.
//
// This is a preview master, not a mixdown: the renders are PRE-FX, so track
// effects, sends and the master chain are not reflected. Panning is not applied
// either — just level and mute, per the "leveled sum" spec.
//
// Pure and dependency-free, so all three packages run exactly this: the
// extension mixes renders off disk in Live, and the viz app mixes samples
// downloaded from Audiotool in a tab. That is also why it decodes AIFF as well
// as WAV — Live's renderPreFxAudio and Audiotool's sample download BOTH hand
// back AIFF under a ".wav" name.

export { faderToGain } from "../mixer.js";

/** A rendered master: 16-bit PCM WAV bytes. */
export interface MasterMix {
  wav: Uint8Array;
}

/** One positioned piece of a track's audio. */
export interface MixSegment {
  label: string;
  /** Encoded WAV/AIFF; null when it couldn't be read. */
  bytes: Uint8Array | null;
  /** Where it sits on the timeline. */
  startSec: number;
  /** The source track's mixer level as a linear gain. */
  gain: number;
}

/**
 * Master mix from positioned segments rather than full-length stems.
 *
 * The per-clip upload path holds one render per contiguous RUN of clips, not
 * one per track, so the master is built by laying those runs back down at their
 * own offsets. Same leveled sum: a stem is only ever a run plus the silence
 * around it, and silence adds nothing.
 */
export function renderMasterMixFromSegments(segments: MixSegment[]): MasterMix | null {
  return mixSegments(segments);
}

function mixSegments(segments: MixSegment[]): MasterMix | null {
  const tDecode = Date.now();
  const decoded = segments
    .map((s) => ({ s, wav: s.bytes ? decodeOrNull(s.bytes, s.label) : null }))
    .filter((d): d is { s: MixSegment; wav: DecodedWav } => {
      if (!d.wav) {
        console.warn(`[Seedbed] master mix: couldn't decode "${d.s.label}" — skipping.`);
        return false;
      }
      return true;
    });
  if (decoded.length === 0) return null;

  // One rate for the mix: the most common among the segments (the higher on a
  // tie). Anything else is converted to it — a clip at 44.1 kHz in a 48 kHz Set
  // (an Audiotool library sample among Seedbed's own) is still part of the song.
  const sampleRate = commonRate(decoded.map((d) => d.wav.sampleRate));
  const usable = decoded.map((d) => {
    if (d.wav.sampleRate === sampleRate) return d;
    console.log(`[Seedbed] master mix: converting "${d.s.label}" from ${d.wav.sampleRate} Hz to ${sampleRate} Hz.`);
    return { s: d.s, wav: resampleAudio(d.wav, sampleRate) };
  });
  if (usable.length === 0) return null;

  console.log(
    `[Seedbed][timing]   master decode (${usable.length} segment(s)): ${Date.now() - tDecode}ms`,
  );

  const offsetOf = (s: MixSegment) => Math.max(0, Math.round(s.startSec * sampleRate));
  const channels = Math.max(...usable.map((d) => d.wav.channels));
  const frames = Math.max(...usable.map((d) => offsetOf(d.s) + d.wav.frames));
  const out = new Float32Array(frames * channels);

  const tSum = Date.now();
  for (const { s, wav } of usable) {
    const g = Number.isFinite(s.gain) && s.gain > 0 ? s.gain : 1;
    const srcCh = wav.channels;
    const offset = offsetOf(s);
    for (let f = 0; f < wav.frames; f++) {
      for (let c = 0; c < channels; c++) {
        // Upmix narrower sources by repeating their last channel (mono → both).
        const sc = c < srcCh ? c : srcCh - 1;
        out[(offset + f) * channels + c]! += wav.samples[f * srcCh + sc]! * g;
      }
    }
  }

  console.log(
    `[Seedbed][timing]   master sum: ${Date.now() - tSum}ms (frames=${frames} ch=${channels})`,
  );

  // A leveled sum can exceed full scale. If it does, scale the whole mix down so
  // the loudest sample sits just under 0 dBFS — relative balance is preserved.
  const tNorm = Date.now();
  let peak = 0;
  for (let i = 0; i < out.length; i++) {
    const a = Math.abs(out[i]!);
    if (a > peak) peak = a;
  }
  if (peak > 1) {
    const norm = 0.99 / peak;
    for (let i = 0; i < out.length; i++) out[i]! *= norm;
  }
  console.log(`[Seedbed][timing]   master normalize: ${Date.now() - tNorm}ms`);

  const tEncode = Date.now();
  const wav = encodeWav16(out, channels, sampleRate);
  console.log(`[Seedbed][timing]   master encode wav: ${Date.now() - tEncode}ms`);

  return { wav };
}

/** The most common rate, the higher one on a tie. */
function commonRate(rates: number[]): number {
  const counts = new Map<number, number>();
  for (const r of rates) counts.set(r, (counts.get(r) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0]![0];
}

/**
 * Convert to another sample rate with 4-point cubic (Catmull-Rom)
 * interpolation — the length in SECONDS is kept, so timing is too. Not a
 * mastering-grade resampler, but free of the zipper noise of a linear one and
 * far better than leaving the audio out.
 */
export function resampleAudio(a: DecodedAudio, rate: number): DecodedAudio {
  if (a.sampleRate === rate || a.frames === 0) return a;
  const ch = a.channels;
  const frames = Math.max(1, Math.round((a.frames * rate) / a.sampleRate));
  const out = new Float32Array(frames * ch);
  const step = a.sampleRate / rate;
  const at = (f: number, c: number): number => a.samples[Math.max(0, Math.min(a.frames - 1, f)) * ch + c]!;
  for (let i = 0; i < frames; i++) {
    const p = i * step;
    const f = Math.floor(p);
    const t = p - f;
    for (let c = 0; c < ch; c++) {
      const y0 = at(f - 1, c), y1 = at(f, c), y2 = at(f + 1, c), y3 = at(f + 2, c);
      out[i * ch + c] = y1 + 0.5 * t * (y2 - y0 + t * (2 * y0 - 5 * y1 + 4 * y2 - y3 + t * (3 * (y1 - y2) + y3 - y0)));
    }
  }
  return { sampleRate: rate, channels: ch, samples: out, frames };
}

// ---------------------------------------------------------------------------
// WAV decode / encode
// ---------------------------------------------------------------------------

/** Decoded PCM — what the mix sums and what the preview encoder consumes. */
export interface DecodedAudio {
  sampleRate: number;
  channels: number;
  /** Interleaved float samples in [-1, 1]; length === frames * channels. */
  samples: Float32Array;
  frames: number;
}
type DecodedWav = DecodedAudio;

const FMT_PCM = 1;
const FMT_FLOAT = 3;
const FMT_EXTENSIBLE = 0xfffe;

function decodeOrNull(bytes: Uint8Array, label: string): DecodedWav | null {
  try {
    return decodeAudio(bytes);
  } catch (e) {
    console.warn(`[Seedbed] master mix: couldn't decode "${label}":`, e);
    return null;
  }
}

/**
 * Decode a stem to interleaved float samples. The SDK's renderPreFxAudio claims
 * to write WAV but actually emits AIFF under a ".wav" name (see import.ts), so we
 * accept both containers and dispatch on the actual magic bytes.
 */
export function decodeAudio(bytes: Uint8Array): DecodedWav | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.byteLength < 12) return null;
  const head = readTag(view, 0);
  const form = readTag(view, 8);
  if (head === "RIFF" && form === "WAVE") return decodeWav(view);
  if (head === "FORM" && (form === "AIFF" || form === "AIFC")) return decodeAiff(view, form);
  return null;
}

/** Parse a RIFF/WAVE view (PCM 8/16/24/32-bit int or 32/64-bit float). */
function decodeWav(view: DataView): DecodedWav | null {
  let fmt: { format: number; channels: number; sampleRate: number; bits: number } | null = null;
  let dataOffset = -1;
  let dataLength = 0;

  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const tag = readTag(view, offset);
    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;
    if (tag === "fmt " && body + 16 <= view.byteLength) {
      let format = view.getUint16(body, true);
      const channels = view.getUint16(body + 2, true);
      const sampleRate = view.getUint32(body + 4, true);
      const bits = view.getUint16(body + 14, true);
      // WAVE_FORMAT_EXTENSIBLE stores the real format in the SubFormat GUID.
      if (format === FMT_EXTENSIBLE && size >= 26 && body + 26 <= view.byteLength) {
        format = view.getUint16(body + 24, true);
      }
      fmt = { format, channels, sampleRate, bits };
    } else if (tag === "data") {
      dataOffset = body;
      dataLength = size;
    }
    offset = body + size + (size & 1); // chunks are word-aligned
  }

  if (!fmt || dataOffset < 0 || fmt.channels <= 0) return null;
  const bytesPerSample = fmt.bits >> 3;
  if (bytesPerSample <= 0) return null;

  // Clamp the declared data size to what's actually present.
  dataLength = Math.min(dataLength, view.byteLength - dataOffset);
  const frameSize = bytesPerSample * fmt.channels;
  const frames = Math.floor(dataLength / frameSize);
  const total = frames * fmt.channels;
  const samples = new Float32Array(total);

  const read = sampleReader(fmt.format, fmt.bits);
  if (!read) {
    console.warn(`[Seedbed] master mix: unsupported WAV format ${fmt.format}/${fmt.bits}-bit.`);
    return null;
  }
  for (let i = 0; i < total; i++) {
    samples[i] = read(view, dataOffset + i * bytesPerSample);
  }

  return { sampleRate: fmt.sampleRate, channels: fmt.channels, samples, frames };
}

/** Returns a per-sample decoder to float [-1,1], or null if unsupported. */
function sampleReader(
  format: number,
  bits: number,
): ((v: DataView, off: number) => number) | null {
  if (format === FMT_FLOAT) {
    if (bits === 32) return (v, o) => v.getFloat32(o, true);
    if (bits === 64) return (v, o) => v.getFloat64(o, true);
    return null;
  }
  if (format === FMT_PCM) {
    if (bits === 8) return (v, o) => (v.getUint8(o) - 128) / 128; // 8-bit is unsigned
    if (bits === 16) return (v, o) => v.getInt16(o, true) / 32768;
    if (bits === 24) {
      return (v, o) => {
        const b0 = v.getUint8(o);
        const b1 = v.getUint8(o + 1);
        const b2 = v.getUint8(o + 2);
        let val = b0 | (b1 << 8) | (b2 << 16);
        if (val & 0x800000) val |= ~0xffffff; // sign-extend 24→32
        return val / 8388608;
      };
    }
    if (bits === 32) return (v, o) => v.getInt32(o, true) / 2147483648;
  }
  return null;
}

/**
 * Parse an AIFF/AIFC view. AIFF stores samples big-endian and the sample rate as
 * an 80-bit extended float; AIFC adds a compression type in COMM ("sowt" = the
 * same PCM byte-swapped to little-endian, "fl32"/"fl64" = float).
 */
function decodeAiff(view: DataView, formType: string): DecodedWav | null {
  let channels = 0;
  let sampleRate = 0;
  let bits = 0;
  let frames = 0;
  let compression = "NONE";
  let dataOffset = -1;
  let dataLength = 0;

  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const tag = readTag(view, offset);
    const size = view.getUint32(offset + 4, false); // AIFF chunks are big-endian
    const body = offset + 8;
    if (tag === "COMM" && body + 18 <= view.byteLength) {
      channels = view.getUint16(body, false);
      frames = view.getUint32(body + 2, false);
      bits = view.getUint16(body + 6, false);
      sampleRate = readExtended80(view, body + 8);
      // AIFC: a 4-char compression type follows the 10-byte sample rate.
      if (formType === "AIFC" && body + 22 <= view.byteLength) {
        compression = readTag(view, body + 18);
      }
    } else if (tag === "SSND") {
      // SSND body = offset(4) + blockSize(4) + sound data. `offset` is a sample
      // skip count, almost always 0.
      const ssndSkip = view.getUint32(body, false);
      dataOffset = body + 8 + ssndSkip;
      dataLength = size - 8 - ssndSkip;
    }
    offset = body + size + (size & 1); // chunks are word-aligned
  }

  if (channels <= 0 || sampleRate <= 0 || dataOffset < 0) return null;
  const bytesPerSample = bits >> 3;
  if (bytesPerSample <= 0) return null;

  dataLength = Math.min(dataLength, view.byteLength - dataOffset);
  const frameSize = bytesPerSample * channels;
  const availFrames = Math.floor(dataLength / frameSize);
  // Trust the actual payload over COMM's declared count if they disagree.
  if (frames <= 0 || frames > availFrames) frames = availFrames;
  const total = frames * channels;
  const samples = new Float32Array(total);

  const read = aiffSampleReader(compression, bits);
  if (!read) {
    console.warn(`[Seedbed] master mix: unsupported AIFF format ${compression}/${bits}-bit.`);
    return null;
  }
  for (let i = 0; i < total; i++) {
    samples[i] = read(view, dataOffset + i * bytesPerSample);
  }

  return { sampleRate, channels, samples, frames };
}

/** Returns a per-sample AIFF/AIFC decoder to float [-1,1], or null if unsupported. */
function aiffSampleReader(
  compression: string,
  bits: number,
): ((v: DataView, off: number) => number) | null {
  const c = compression.toLowerCase();
  if (c === "fl32") return (v, o) => v.getFloat32(o, false);
  if (c === "fl64") return (v, o) => v.getFloat64(o, false);
  // "NONE"/"twos" = big-endian PCM; "sowt" = the same byte-swapped to little-endian.
  if (c !== "none" && c !== "twos" && c !== "sowt") return null;
  const le = c === "sowt";
  if (bits === 8) return (v, o) => v.getInt8(o) / 128; // AIFF 8-bit is signed
  if (bits === 16) return (v, o) => v.getInt16(o, le) / 32768;
  if (bits === 24) {
    return (v, o) => {
      const b0 = v.getUint8(o);
      const b1 = v.getUint8(o + 1);
      const b2 = v.getUint8(o + 2);
      let val = le ? b0 | (b1 << 8) | (b2 << 16) : (b0 << 16) | (b1 << 8) | b2;
      if (val & 0x800000) val |= ~0xffffff; // sign-extend 24→32
      return val / 8388608;
    };
  }
  if (bits === 32) return (v, o) => v.getInt32(o, le) / 2147483648;
  return null;
}

/** Decode an 80-bit IEEE-754 extended float (AIFF sample rate), big-endian. */
function readExtended80(view: DataView, off: number): number {
  const exponent = ((view.getUint8(off) & 0x7f) << 8) | view.getUint8(off + 1);
  const mantissa = view.getUint32(off + 2, false) * 2 ** 32 + view.getUint32(off + 6, false);
  if (exponent === 0 && mantissa === 0) return 0;
  const sign = view.getUint8(off) & 0x80 ? -1 : 1;
  return sign * mantissa * 2 ** (exponent - 16383 - 63);
}

/** Encode interleaved float samples as a 24-bit PCM WAV — for renders worth keeping at full depth. */
export function encodeWav24(samples: Float32Array, channels: number, sampleRate: number): Uint8Array {
  const blockAlign = channels * 3;
  const dataSize = samples.length * 3;
  const buffer = new ArrayBuffer(44 + dataSize + (dataSize & 1));
  const view = new DataView(buffer);
  writeTag(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize + (dataSize & 1), true);
  writeTag(view, 8, "WAVE");
  writeTag(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, FMT_PCM, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 24, true);
  writeTag(view, 36, "data");
  view.setUint32(40, dataSize, true);
  let o = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    let v = Math.round(s * 8388607);
    if (v < 0) v += 0x1000000;
    view.setUint8(o, v & 0xff);
    view.setUint8(o + 1, (v >> 8) & 0xff);
    view.setUint8(o + 2, (v >> 16) & 0xff);
    o += 3;
  }
  return new Uint8Array(buffer);
}

/** Encode interleaved float samples as a 16-bit PCM WAV. */
export function encodeWav16(samples: Float32Array, channels: number, sampleRate: number): Uint8Array {
  const blockAlign = channels * 2;
  const dataSize = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeTag(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeTag(view, 8, "WAVE");
  writeTag(view, 12, "fmt ");
  view.setUint32(16, 16, true); // PCM fmt chunk size
  view.setUint16(20, FMT_PCM, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeTag(view, 36, "data");
  view.setUint32(40, dataSize, true);

  let o = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    view.setInt16(o, Math.round(s * 32767), true);
    o += 2;
  }
  return new Uint8Array(buffer);
}

function readTag(view: DataView, offset: number): string {
  return (
    String.fromCharCode(view.getUint8(offset)) +
    String.fromCharCode(view.getUint8(offset + 1)) +
    String.fromCharCode(view.getUint8(offset + 2)) +
    String.fromCharCode(view.getUint8(offset + 3))
  );
}

function writeTag(view: DataView, offset: number, tag: string): void {
  for (let i = 0; i < 4; i++) view.setUint8(offset + i, tag.charCodeAt(i));
}
